import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaSearch } from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import useMediaQuery from './shared/useMediaQuery';
import { normalizeForSearch } from '../utils/slugify';
import { cacheOverview, getRecentBuildings, readCachedOverview } from '../utils/recentBuildings';
import '../styles/Home.css';

const API_URL = process.env.REACT_APP_API_URL;
const AUDITS_SHOWN = 8;
const RECENT_SHOWN = 5;
const RESULTS_PER_GROUP = { clients: 4, buildings: 7 };

const dateFormat = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
const todayFormat = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

function formatAuditDate(value) {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : dateFormat.format(date);
}

function formatVisit(timestamp) {
    const minutes = Math.round((Date.now() - timestamp) / 60000);
    if (minutes < 1) return "à l'instant";
    if (minutes < 60) return `il y a ${minutes} min`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `il y a ${hours} h`;
    const days = Math.round(hours / 24);
    return days === 1 ? 'hier' : `il y a ${days} jours`;
}

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const dashboardPath = (building) => `/client/${building.client_id}/${building._id}`;
const auditPath = (building) => `/audit/${building.client_id}/${building._id}`;

/* ─────────────────────────────────────────────
   Page d'accueil : salutation, recherche de tout client ou bâtiment, « Mes audits »
   (bâtiments où le compte figure parmi les auditeurs ; pour un compte client, ses
   bâtiments) et « Récemment ouverts » (sur cet appareil, cf. utils/recentBuildings).
   Données : GET /home_overview, filtré côté API selon le compte, gardé en cache local
   pour que la page s'ouvre aussi hors-ligne.
───────────────────────────────────────────── */
const MainPage = () => {
    const { user, status, isStaff } = useAuth();
    const navigate = useNavigate();
    const [overview, setOverview] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [fromCache, setFromCache] = useState(false);
    const userId = user?._id;

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const response = await fetch(`${API_URL}/home_overview`);
            if (!response.ok) throw new Error(`erreur ${response.status}`);
            const data = await response.json();
            setOverview(data);
            setFromCache(false);
            cacheOverview(data);
        } catch (error) {
            const cached = readCachedOverview();
            if (cached) {
                setOverview(cached);
                setFromCache(true);
            } else {
                setLoadError(error.message);
            }
        }
    }, []);

    useEffect(() => {
        if (status === 'ready') load();
    }, [status, userId, load]);

    const buildingsById = useMemo(
        () => Object.fromEntries((overview?.buildings || []).map((building) => [building._id, building])),
        [overview]
    );

    const isClient = overview?.scope === 'client';
    const canEdit = !user || isStaff || user.role === 'client_editeur';
    const firstName = user?.name?.trim().split(/\s+/)[0];

    let primaryAction = null;
    if (!user) {
        primaryAction = <Link className="home-primary-action" to="/login?next=/">Se connecter</Link>;
    } else if (isStaff) {
        primaryAction = <button type="button" className="home-primary-action" onClick={() => navigate('/audit')}>Nouvel audit</button>;
    } else if (user.role === 'client_editeur' && user.client_ids.length === 1) {
        primaryAction = (
            <button type="button" className="home-primary-action" onClick={() => navigate(`/audit/${user.client_ids[0]}`)}>
                Nouveau bâtiment
            </button>
        );
    }

    return (
        <div className="home">
            <header className="home-hero">
                <div className="home-greeting">
                    <h1>{firstName ? `Bonjour ${firstName}` : 'Bienvenue sur Walter'}</h1>
                    <p>{user ? capitalize(todayFormat.format(new Date())) : 'Connectez-vous pour retrouver vos audits.'}</p>
                </div>
                {primaryAction}
            </header>

            <HomeSearch overview={overview} isClient={isClient} />

            {fromCache && <p className="home-notice">Hors connexion : dernière liste connue sur cet appareil.</p>}
            {loadError && (
                <p className="home-notice home-notice--error">
                    Impossible de charger la liste des bâtiments ({loadError}).{' '}
                    <button type="button" className="home-link-button" onClick={load}>Réessayer</button>
                </p>
            )}

            <div className={`home-body${user ? '' : ' home-body--single'}`}>
                {user && (
                    <AuditList
                        title={isClient ? 'Vos bâtiments' : 'Mes audits'}
                        buildings={overview ? overview.buildings.filter((building) => building.mine) : null}
                        email={user.email}
                        isClient={isClient}
                        canEdit={canEdit}
                    />
                )}
                <RecentList buildingsById={buildingsById} loaded={Boolean(overview)} />
            </div>
        </div>
    );
};

/* Recherche instantanée dans les clients et bâtiments visibles par le compte.
   Clavier : « / » pour y aller, flèches pour choisir, Entrée pour ouvrir, Échap pour vider. */
const HomeSearch = ({ overview, isClient }) => {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const listId = useId();
    const isPhone = useMediaQuery('(max-width: 640px)');
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);

    useEffect(() => {
        const handleSlash = (event) => {
            const target = event.target;
            const typing = target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
            if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey) {
                event.preventDefault();
                inputRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handleSlash);
        return () => document.removeEventListener('keydown', handleSlash);
    }, []);

    const { results, hiddenCount } = useMemo(() => {
        const needle = normalizeForSearch(query.trim());
        if (!needle || !overview) return { results: [], hiddenCount: 0 };
        const matchingClients = isClient ? [] : overview.clients
            .filter((client) => normalizeForSearch(client.name).includes(needle));
        const matchingBuildings = overview.buildings
            .filter((building) => [building.name, building.city, building.client_name]
                .some((field) => normalizeForSearch(field).includes(needle)));
        const clients = matchingClients
            .slice(0, RESULTS_PER_GROUP.clients)
            .map((client) => ({
                key: `c-${client._id}`, kind: 'Client', title: client.name, detail: null, path: `/client/${client._id}`,
            }));
        const buildings = matchingBuildings
            .slice(0, RESULTS_PER_GROUP.buildings)
            .map((building) => ({
                key: `b-${building._id}`, kind: 'Bâtiment', title: building.name,
                detail: [building.client_name, building.city].filter(Boolean), path: dashboardPath(building),
            }));
        return {
            results: [...clients, ...buildings],
            hiddenCount: matchingClients.length + matchingBuildings.length - clients.length - buildings.length,
        };
    }, [query, overview, isClient]);

    useEffect(() => setActive(0), [query]);

    const open = (result) => {
        if (result) navigate(result.path);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown' && results.length) {
            event.preventDefault();
            setActive((index) => (index + 1) % results.length);
        } else if (event.key === 'ArrowUp' && results.length) {
            event.preventDefault();
            setActive((index) => (index - 1 + results.length) % results.length);
        } else if (event.key === 'Enter') {
            open(results[active]);
        } else if (event.key === 'Escape') {
            setQuery('');
        }
    };

    const showPanel = query.trim().length > 0;

    return (
        <div className="home-search">
            <div className="home-search-field">
                <FaSearch aria-hidden="true" />
                <input
                    ref={inputRef}
                    type="search"
                    role="combobox"
                    aria-expanded={showPanel}
                    aria-controls={listId}
                    aria-activedescendant={showPanel && results[active] ? `${listId}-${active}` : undefined}
                    aria-label="Rechercher un client ou un bâtiment"
                    placeholder={isPhone
                        ? (isClient ? 'Bâtiment ou ville' : 'Client, bâtiment ou ville')
                        : (isClient ? 'Rechercher un bâtiment ou une ville' : 'Rechercher un client, un bâtiment ou une ville')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                />
                {!query && <kbd className="home-search-key" aria-hidden="true">/</kbd>}
            </div>
            {showPanel && (
                <ul className="home-search-results" id={listId} role="listbox">
                    {!overview && <li className="home-search-empty">Chargement…</li>}
                    {overview && results.length === 0 && (
                        <li className="home-search-empty">Aucun client ni bâtiment ne correspond à « {query.trim()} ».</li>
                    )}
                    {results.map((result, index) => (
                        <li
                            key={result.key}
                            id={`${listId}-${index}`}
                            role="option"
                            aria-selected={index === active}
                            className={`home-search-result${index === active ? ' is-active' : ''}`}
                            onMouseEnter={() => setActive(index)}
                            onMouseDown={(e) => { e.preventDefault(); open(result); }}
                        >
                            <span className="home-search-kind">{result.kind}</span>
                            <span className="home-search-title">{result.title}</span>
                            {result.detail && (
                                <span className="home-search-detail">
                                    {result.detail.map((part) => <span key={part}>{part}</span>)}
                                </span>
                            )}
                        </li>
                    ))}
                    {hiddenCount > 0 && (
                        <li className="home-search-empty" role="presentation">
                            {hiddenCount} autre{hiddenCount > 1 ? 's' : ''} résultat{hiddenCount > 1 ? 's' : ''} : précisez la recherche.
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
};

const BuildingMeta = ({ building }) => (
    <span className="home-row-meta">
        {building.client_name && <span>{building.client_name}</span>}
        {building.city && <span className="home-row-city"><FaMapMarkerAlt aria-hidden="true" />{building.city}</span>}
    </span>
);

/* « Mes audits » (staff) ou « Vos bâtiments » (client), du plus récent au plus ancien. */
const AuditList = ({ title, buildings, email, isClient, canEdit }) => {
    const [expanded, setExpanded] = useState(false);
    const sorted = useMemo(
        () => (buildings || []).slice().sort((a, b) => (b.audit_date || '').localeCompare(a.audit_date || '')),
        [buildings]
    );
    const shown = expanded ? sorted : sorted.slice(0, AUDITS_SHOWN);

    return (
        <section className="home-section home-audits" aria-labelledby="home-audits-title">
            <h2 id="home-audits-title">
                {title}
                {buildings && <span className="home-count">{buildings.length}</span>}
            </h2>
            {buildings === null ? (
                <p className="home-empty">Chargement…</p>
            ) : sorted.length === 0 ? (
                <p className="home-empty">
                    {isClient
                        ? "Aucun bâtiment n'est encore rattaché à votre compte."
                        : <>Aucun bâtiment ne vous compte parmi ses auditeurs. Un audit apparaît ici quand {email} est
                            renseigné comme auditeur dans les informations du bâtiment. <Link to="/clients">Voir tous les clients</Link></>}
                </p>
            ) : (
                <>
                    <ul className="home-list">
                        {shown.map((building) => (
                            <li key={building._id} className="home-row">
                                <Link className="home-row-main" to={dashboardPath(building)} title="Ouvrir le tableau de bord">
                                    <span className="home-row-name">{building.name || 'Bâtiment sans nom'}</span>
                                    <BuildingMeta building={building} />
                                </Link>
                                <span className={`home-row-date${building.audit_date ? '' : ' is-missing'}`}>
                                    {formatAuditDate(building.audit_date) || 'Date à saisir'}
                                </span>
                                {canEdit && <Link className="home-row-action" to={auditPath(building)}>Saisie</Link>}
                            </li>
                        ))}
                    </ul>
                    {sorted.length > AUDITS_SHOWN && (
                        <button type="button" className="home-link-button home-more" onClick={() => setExpanded((value) => !value)}>
                            {expanded ? 'Afficher moins' : `Afficher les ${sorted.length - AUDITS_SHOWN} autres`}
                        </button>
                    )}
                </>
            )}
        </section>
    );
};

/* Bâtiments ouverts récemment sur cet appareil, chacun ramenant à la dernière page ouverte
   pour lui ; ceux que le compte ne voit plus sont ignorés. */
const RecentList = ({ buildingsById, loaded }) => {
    const recent = useMemo(
        () => getRecentBuildings()
            .filter((entry) => buildingsById[entry.id])
            .slice(0, RECENT_SHOWN)
            .map((entry) => ({ ...buildingsById[entry.id], visitedAt: entry.at, lastPath: entry.path })),
        [buildingsById]
    );

    return (
        <section className="home-section home-recent" aria-labelledby="home-recent-title">
            <h2 id="home-recent-title">Récemment ouverts</h2>
            {!loaded ? null : recent.length === 0 ? (
                <p className="home-empty">Les bâtiments que vous ouvrez sur cet appareil s'afficheront ici.</p>
            ) : (
                <ul className="home-recent-list">
                    {recent.map((building) => (
                        <li key={building._id}>
                            <Link className="home-recent-item" to={building.lastPath || dashboardPath(building)}>
                                <span className="home-row-name">{building.name || 'Bâtiment sans nom'}</span>
                                <span className="home-recent-detail">
                                    <span>{building.client_name}</span>
                                    <span className="home-recent-time">{formatVisit(building.visitedAt)}</span>
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export { HomeSearch };
export default MainPage;
